import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useDemo } from '../../contexts/DemoContext';
import { BackLink, DemoFooter, LenticMark } from '../../components/demo/DemoChrome';
import { SourceCard } from '../../components/demo/SourceCard';
import { AnalysisLine, SOURCE_ID, analysisLines, samplePosting } from '../../data/demo';
import { readRoleMatch, readUrlSignal, stripQuery } from '../../utils/urlSignal';
import { deriveListingTitle } from '../../utils/listingTitle';
export function RoleAnalysis() {
  const {
    postingUrl,
    postingText,
    usedSample,
    offLibrary,
    mode
  } = useDemo();
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  // A pasted URL gets read first — the system reacts to their input before anything else.
  const custom = !usedSample && (postingUrl.trim().length > 0 || postingText.trim().length > 0);
  const signal = useMemo(() => readUrlSignal(postingUrl), [postingUrl]);
  const roleMatch = useMemo(() => readRoleMatch(postingUrl), [postingUrl]);
  const lines: AnalysisLine[] = useMemo(() => {
    if (!custom) return analysisLines;
    // An adjacent role shows its reasoning rather than quietly asserting a match.
    const body = analysisLines.map(line => line.label === 'Role match' && roleMatch.adjacent && roleMatch.urlRole ? {
      ...line,
      values: [`${roleMatch.urlRole} → Financial Controller`],
      status: 'adjacent role',
      note: 'Same instrument: close, reconciliations, variance. Seniority calibrated at configuration.'
    } : line);
    return [{
      label: 'URL signal',
      values: [signal.token],
      status: signal.matched ? 'matched' : '—'
    }, ...body];
  }, [custom, signal, roleMatch]);
  useEffect(() => {
    if (step >= lines.length) return;
    const t = window.setTimeout(() => setStep(s => s + 1), step === 0 ? 450 : 800);
    return () => window.clearTimeout(t);
  }, [step, lines.length]);
  const done = step >= lines.length;
  const activePhrases = custom ? Math.max(0, step - 1) : step;
  const displayUrl = postingUrl.trim() ? stripQuery(postingUrl) : samplePosting.url;
  const chipTitle = custom ? deriveListingTitle(postingUrl) : samplePosting.title;
  return <div className="flex min-h-full w-full flex-col bg-paper" data-id="element-725" data-mp="l6o8yohxjp7c">
      <div className="flex items-center justify-between px-8 pt-8" data-id="element-726" data-mp="l668tgs5cqu5">
        <LenticMark data-id="element-727" data-mp="g8watcqoz6up" />
        <BackLink to="/demo" label="Change the posting" data-id="element-728" data-mp="g8mrg805jxxz" />
      </div>

      <main className="mx-auto w-full max-w-[1400px] flex-1 px-8 py-10" data-id="element-729" data-mp="bg32yfzxfiee">
        <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="inline-flex max-w-full items-center gap-3 overflow-hidden whitespace-nowrap rounded-sm border border-rule bg-white px-4 py-2.5" data-id="element-730" data-mp="7ft0vef99c4t">
          <span className="shrink-0 font-mono-num text-[11px] text-brand" data-id="element-731" data-mp="2huid2nw4stwv">{SOURCE_ID}</span>
          <span className="h-3.5 w-px shrink-0 bg-rule" data-id="element-732" data-mp="2mtlapn9s7xut" />
          <span className="max-w-[360px] truncate text-[13px] font-medium text-ink" data-id="element-733" data-mp="2qu56onimcvfd">
            {chipTitle}
          </span>
          <span className="min-w-0 truncate font-mono-num text-[11px] text-muted/80" data-id="element-734" data-mp="2vtrmn4pu0prv">
            {displayUrl}
          </span>
          {custom && <span className="shrink-0 rounded-sm border border-rule bg-paper px-2 py-0.5 font-mono-num text-[10.5px] uppercase tracking-wider text-muted" data-id="element-735" data-mp="30q8k4pix8ghm">
              Your listing
            </span>}
        </motion.div>

        {offLibrary && <p className="mt-4 max-w-2xl rounded-sm border-l-2 border-signal-amber bg-white px-4 py-3 text-[13px] leading-relaxed text-ink" data-id="element-736" data-mp="3eiodeym0t05c">
            This posting maps to a role family outside the current library (FIN only in this demo).
            Showing the Financial Controller flow.
          </p>}

        <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,40fr)_minmax(0,60fr)]" data-id="element-737" data-mp="3atvn2sa4wvoi">
          <SourceCard activePhrases={activePhrases} representative={custom} data-id="element-738" data-mp="35v280vf78wm8" />

          <div data-id="element-739" data-mp="3kljeit6oycpz">
            <p className="eyebrow text-brand" data-id="element-740" data-mp="3kjyh8vszsfof">Role analysis</p>

            {custom && <p className="mt-2.5 max-w-lg font-mono-num text-[11px] leading-relaxed text-muted/80" data-id="element-741" data-mp="3fln9qrq66h9d">
                Demo maps your URL to the closest library role and analyses a representative
                posting. Production fetches and parses the posting itself.
              </p>}
            {mode === 'single' && <p className="mt-2.5 font-mono-num text-[11px] leading-relaxed text-muted/80" data-id="element-742" data-mp="31vtf1srzh2rq">
                single-candidate probe · CV weighting applied at configuration
              </p>}

            <dl className="mt-7 divide-y divide-rule border-t border-rule" data-id="element-743" data-mp="h6dwfl2x4kd6">
              {lines.map((line, i) => {
              const visible = i < step;
              const isSignal = custom && i === 0;
              return <motion.div key={line.label} initial={false} animate={{
                opacity: visible ? 1 : 0.12,
                y: visible ? 0 : 4
              }} transition={{
                duration: 0.2,
                ease: [0.23, 1, 0.32, 1]
              }} className="grid grid-cols-[180px_minmax(0,1fr)_auto] items-start gap-4 py-6" data-id="element-744" data-mp="oobqqicj3ovt" data-mpi={i}>
                    <dt className="font-mono-num text-[11px] uppercase tracking-[0.1em] text-muted" data-id="element-745" data-mp="17ea85p27uuc4">
                      {line.label}
                    </dt>
                    <dd className="space-y-1.5" data-id="element-746" data-mp="tmdjjuzrb2kh">
                      {line.values.map(v => <p key={v} className={`leading-relaxed ${isSignal ? 'font-mono-num text-[13px] text-ink' : 'text-[14px] text-ink'}`} data-id="element-747" data-mp="xmxhv1w2gc87">
                          {line.values.length > 1 && <span className="text-muted" data-id="element-748" data-mp="1zzyviwc5pidm">— </span>}
                          {v}
                        </p>)}
                      {line.note && <p className="max-w-md pt-1 font-mono-num text-[11px] leading-relaxed text-muted/80" data-id="element-749" data-mp="1qys9k7qqnfcz">
                          {line.note}
                        </p>}
                    </dd>
                    <dd data-id="element-750" data-mp="1h40dx9gpv2ku">
                      {line.status === '—' ? <span className="inline-flex items-center rounded-sm border border-rule bg-paper px-2 py-0.5 font-mono-num text-[10.5px] uppercase tracking-wider text-muted" data-id="element-751" data-mp="1cae0vg6n4woi">
                          —
                        </span> : line.status && <span className="inline-flex items-center gap-1.5 rounded-sm border border-brand/30 bg-brand-tint px-2 py-0.5 font-mono-num text-[10.5px] uppercase tracking-wider text-brand" data-id="element-752" data-mp="yamqdpfc1yf9">
                            <CheckIcon className="h-3 w-3" data-id="element-753" data-mp="j84u6ivtaw3o" />
                            {line.status}
                          </span>}
                    </dd>
                  </motion.div>;
            })}
            </dl>

            <motion.div initial={false} animate={{
            opacity: done ? 1 : 0
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-754" data-mp="y559to0qb0ls">
              <p className="mt-7 max-w-lg text-[13.5px] leading-relaxed text-muted" data-id="element-755" data-mp="5hrkoc39v9c8">
                The posting selects from the library. It never writes the assessment — that
                distinction is the product.
              </p>
              <button type="button" onClick={() => navigate('/demo/configure')} disabled={!done} className="group mt-8 inline-flex items-center gap-2 rounded-sm bg-brand px-6 py-3.5 text-[14px] font-semibold text-white transition-colors duration-150 ease-settle hover:bg-brand-dark disabled:cursor-not-allowed" data-id="element-756" data-mp="f8m99n3zql0u">
                Configure the assessment
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-settle group-hover:translate-x-0.5" data-id="element-757" data-mp="1avnzju7uykud" />
              </button>
            </motion.div>
          </div>
        </div>
      </main>

      <DemoFooter data-id="element-758" data-mp="1jurhuvz7aw7t" />
    </div>;
}